import { ThemeEvents } from '@theme/events';

/**
 * <oppie-cart-timer> — a soft "your cart is reserved" countdown.
 *
 * Behaviour (agreed with the merchant):
 * - Starts on the first item added to the cart and persists in localStorage, so
 *   it survives reloads and does NOT reset on subsequent cart changes.
 * - Counts down once and hides itself at 00:00 (one-shot). The cart itself is
 *   never cleared — Shopify holds no inventory until checkout, so this is a
 *   gentle reassurance, not a hard reservation.
 * - The stored timestamp is cleared when the cart becomes empty, so the next
 *   session starts fresh at the full duration.
 *
 * The element only renders in the has-items cart state (inside cart-summary),
 * so its presence already implies a non-empty cart.
 */
const DEFAULT_STORAGE_KEY = 'oppie_cart_reserved_at';

class OppieCartTimer extends HTMLElement {
  #interval = null;

  connectedCallback() {
    this.valueEl = this.querySelector('[data-timer-value]');
    this.storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
    this.duration = (parseInt(this.dataset.duration, 10) || 600) * 1000;

    this.#start();
  }

  disconnectedCallback() {
    if (this.#interval) clearInterval(this.#interval);
  }

  #start() {
    const deadline = this.#resolveDeadline();
    this.#tick(deadline);
    if (this.#interval) clearInterval(this.#interval);
    this.#interval = setInterval(() => this.#tick(deadline), 1000);
  }

  /**
   * Returns the reservation deadline (ms epoch). Reuses a stored start time when
   * present so the countdown continues from the first add; otherwise seeds it.
   */
  #resolveDeadline() {
    let startedAt = null;
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) startedAt = parseInt(stored, 10);
    } catch {
      /* storage unavailable (private mode) — fall back to a fresh in-memory start */
    }

    if (!startedAt || Number.isNaN(startedAt)) {
      startedAt = Date.now();
      try {
        localStorage.setItem(this.storageKey, String(startedAt));
      } catch {
        /* non-blocking */
      }
    }

    return startedAt + this.duration;
  }

  #tick(deadline) {
    const remaining = deadline - Date.now();

    if (remaining <= 0) {
      this.hidden = true;
      if (this.#interval) clearInterval(this.#interval);
      this.#interval = null;
      return;
    }

    const totalSeconds = Math.round(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (this.valueEl) {
      this.valueEl.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
    this.hidden = false;
  }
}

if (!customElements.get('oppie-cart-timer')) {
  customElements.define('oppie-cart-timer', OppieCartTimer);
}

/**
 * Clear the stored reservation start when the cart empties, so the next add
 * begins a fresh countdown. Registered once at module load (the module is loaded
 * globally from the header, like the upsell rail), independent of whether a
 * <oppie-cart-timer> is currently in the DOM.
 */
if (!window.__oppieCartTimerClearBound) {
  window.__oppieCartTimerClearBound = true;
  // Het event draagt de nieuwe cart al mee (event.detail.resource); alleen
  // als die ontbreekt halen we hem op. Scheelt een /cart.js-call per wijziging.
  document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
    try {
      let itemCount = event.detail?.resource?.item_count;
      if (typeof itemCount !== 'number') {
        const cart = await (await fetch(`${Theme.routes.cart_url || '/cart'}.js`)).json();
        itemCount = cart ? cart.item_count : 0;
      }
      if (itemCount === 0) localStorage.removeItem(DEFAULT_STORAGE_KEY);
    } catch {
      /* non-blocking */
    }
  });
}
